import React, { useState, useEffect } from 'react'; // v2
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import supabase from './supabaseClient';
import SEOMeta from './SEOMeta';
import { slugify, unslugify } from './slugUtils';

const parsePrice = (val) => parseFloat(String(val).replace(/[^0-9.]/g, ''));
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const isMobileCheck = () => window.innerWidth <= 768;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatPrice = (val) => {
  if (!val && val !== 0) return '—';
  const num = parsePrice(val);
  if (isNaN(num)) return val;
  return '$' + num.toFixed(2);
};

const COLORS = {
  'All': '#2c4a2e', '2': '#2196F3', '3': '#4CAF50', '4': '#FF9800',
  '6': '#E91E63', '8': '#9C27B0', '10': '#F44336', '12': '#00BCD4', 'TC': '#795548', 'Plug': '#607D8B',
};

const isTc = (l) => l.sale_type && l.sale_type.toLowerCase().includes('tissue');
const isPlug = (l) => l.sale_type && l.sale_type.toLowerCase().includes('plug');
const getSize = (l) => isTc(l) ? 'TC' : isPlug(l) ? 'Plug' : l.sale_size;

const PlantDetails = () => {
  const { slug, plantName } = useParams();
  const navigate = useNavigate();
  const [resolvedName, setResolvedName] = useState('');
  // Support both the new /plant/:slug route and the legacy /plant-details/:plantName route
  const routeKey = slug || plantName;
  const isLegacyRoute = !slug && !!plantName;
  const [listings, setListings] = useState([]);
  const [memberSales, setMemberSales] = useState([]);
  const [headerPhoto, setHeaderPhoto] = useState(null);
  const [relatedPlants, setRelatedPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [etsyListings, setEtsyListings] = useState([]);
  const [etsyLoading, setEtsyLoading] = useState(false);
  const [etsyError, setEtsyError] = useState(null);
  const [ebayListings, setEbayListings] = useState([]);
  const [ebayLoading, setEbayLoading] = useState(false);
  const [ebayError, setEbayError] = useState(null);
  const [storeSightings, setStoreSightings] = useState([]);
  const [forSaleListings, setForSaleListings] = useState([]);
  const [forSaleLoading, setForSaleLoading] = useState(false);
  const [pdMessageModal, setPdMessageModal] = useState(null);
  const [pdMsgForm, setPdMsgForm] = useState({ buyer_name: '', buyer_email: '', message: '' });
  const [pdMsgSending, setPdMsgSending] = useState(false);
  const [pdMsgSent, setPdMsgSent] = useState(false);
  const [pdMsgError, setPdMsgError] = useState('');
  const [storeLoading, setStoreLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('mine');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedSize, setSelectedSize] = useState('All');
  const [sortOrder, setSortOrder] = useState('newest');
  const [filterType, setFilterType] = useState('All');
  const [filterSize, setFilterSize] = useState('All');
  const [filterPattern, setFilterPattern] = useState('All');
  const [visibleRecords, setVisibleRecords] = useState(10);
  const [mobile, setMobile] = useState(isMobileCheck());

  useEffect(() => {
    const handleResize = () => setMobile(isMobileCheck());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const searchQuery_ = isLegacyRoute ? decodeURIComponent(plantName) : unslugify(slug);
  // 'decoded' is kept as the display name once resolved from the DB; falls back to slug-derived text
  const decoded = resolvedName || searchQuery_;
  // Build a flexible ilike pattern: words joined with % wildcards so slugs that
  // dropped punctuation (e.g. apostrophes) still match the real common_name.
  // For legacy routes use the exact decoded name.
  // For slug routes: once resolvedName is set use exact match to avoid
  // over-matching (e.g. monstera-albo matching Monstera Adansonii Albo too).
  // Before resolution, use an exact match on the unslugified text which works
  // for most names; names with apostrophes fall back to the first DB hit.
  const ilikePattern = isLegacyRoute
    ? searchQuery_.trim()
    : resolvedName || searchQuery_.trim();

  useEffect(() => {
    document.title = `${decoded} Price Guide - True Plant Value`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', `See real sold prices for ${decoded}. Track price history, size breakdowns, and tissue culture pricing from actual plant collectors on True Plant Value.`);
    return () => { document.title = 'True Plant Value'; };
  }, [decoded]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setListings([]);
    setMemberSales([]);
    setRelatedPlants([]);
    setResolvedName('');
    setHeaderPhoto(null);
    setLoading(true);
    setVisibleRecords(10);
  }, [routeKey]);

  useEffect(() => {
    const fetchHeaderPhoto = async () => {
      const { data } = await supabase.from('plant_catalog').select('header_image_url').ilike('common_name', ilikePattern).limit(1);
      if (data && data.length > 0) setHeaderPhoto(data[0].header_image_url);
    };
    fetchHeaderPhoto();

    const fetchListings = async () => {
      // First try exact match on the unslugified text
      const exactQuery = searchQuery_.trim();
      let { data, error } = await supabase
        .from('plants')
        .select('*')
        .ilike('common_name', exactQuery)
        .eq('status', 'approved')
        .order('date_sold', { ascending: false });
      // If no results (e.g. apostrophe stripped by slugify), try word-by-word wildcard
      if (!error && (!data || data.length === 0) && !isLegacyRoute) {
        const wildcardPattern = '%' + exactQuery.split(/\s+/).join('%') + '%';
        const { data: fuzzyData, error: fuzzyError } = await supabase
          .from('plants')
          .select('*')
          .ilike('common_name', wildcardPattern)
          .eq('status', 'approved')
          .order('date_sold', { ascending: false });
        if (!fuzzyError) data = fuzzyData;
      }
      if (error) console.error('Error fetching listings:', error);
      else {
        // Pin to exact name from first result to avoid mixing different plants
        if (data && data.length > 0 && data[0].common_name) {
          const pinnedName = data[0].common_name;
          setResolvedName(pinnedName);
          // Filter to only exact matches now that we know the real name
          data = data.filter(d => d.common_name.toLowerCase() === pinnedName.toLowerCase());
        }
        setListings(data);
      }
      setLoading(false);

      if (data && data.length > 0 && data[0].genus) {
        const currentName = data[0].common_name?.toLowerCase() || '';
        // Get key words from the current plant name to score similarity
        const currentWords = currentName.split(/\s+/).filter(w => w.length > 3);
        const { data: related } = await supabase
          .from('plants')
          .select('common_name, sold_price, genus, image_url')
          .eq('status', 'approved')
          .ilike('genus', data[0].genus)
          .not('common_name', 'ilike', decoded.trim());
        if (related) {
          const seen = new Set();
          const unique = related.filter(p => {
            const name = p.common_name?.trim();
            if (!name || seen.has(name.toLowerCase())) return false;
            seen.add(name.toLowerCase());
            return true;
          });
          // Score by how many words from the current plant name appear in related plant name
          const scored = unique.map(p => {
            const pName = p.common_name.toLowerCase();
            const score = currentWords.filter(w => pName.includes(w)).length;
            return { ...p, _score: score };
          });
          // Sort: highest word-match score first, then alphabetical
          scored.sort((a, b) => b._score - a._score || a.common_name.localeCompare(b.common_name));
          setRelatedPlants(scored.slice(0, 5));
        }
      }
    };
    fetchListings();

    // Fetch member sold inventory for this plant
    const fetchMemberSales = async () => {
      const { data, error } = await supabase
        .from('inventory')
        .select('id, common_name, price, sale_type, sale_size, date_sold, image_url, notes')
        .ilike('common_name', ilikePattern)
        .eq('status', 'sold')
        .not('date_sold', 'is', null)
        .order('date_sold', { ascending: false });
      if (!error && data) setMemberSales(data);
    };
    fetchMemberSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // Combine listings + member sales for chart and stats
  // Normalize member sales to match listing shape
  const normalizedMemberSales = memberSales.map(m => ({
    ...m,
    sold_price: m.price,
    date_sold: m.date_sold,
    source: 'Member Sale',
    _isMember: true,
  }));

  const allSales = [...listings, ...normalizedMemberSales];

  const availableSizes = ['All', ...new Set(
    allSales.filter(l => l.sold_price || l.price).map(l => getSize(l)).filter(Boolean)
  )].sort((a, b) => {
    if (a === 'All') return -1; if (b === 'All') return 1;
    const aNum = !isNaN(parseFloat(a)); const bNum = !isNaN(parseFloat(b));
    if (aNum && bNum) return parseFloat(a) - parseFloat(b);
    if (aNum) return -1; if (bNum) return 1;
    if (a === 'Plug') return -1; if (b === 'Plug') return 1;
    return 0;
  });

  const sizeCounts = allSales.reduce((acc, l) => {
    const size = getSize(l);
    if (size) acc[size] = (acc[size] || 0) + 1;
    return acc;
  }, {});
  const mostSoldSize = Object.entries(sizeCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

  const uniqueSizes = [...new Set(allSales.filter(l => l.sold_price || l.price).map(l => getSize(l)).filter(Boolean))];

  const getChartData = () => {
    let filtered = allSales.filter(l => l.date_sold && (l.sold_price || l.price));
    if (selectedSize === 'TC') {
      filtered = filtered.filter(l => isTc(l));
    } else if (selectedSize === 'Plug') {
      filtered = filtered.filter(l => isPlug(l));
    } else if (selectedSize !== 'All') {
      filtered = filtered.filter(l => l.sale_size === selectedSize && !isTc(l) && !isPlug(l));
    }
    const sorted = filtered
      .map(l => ({ date: l.date_sold, price: parsePrice(l.sold_price || l.price), size: getSize(l) }))
      .filter(l => !isNaN(l.price))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return sorted.map((l, idx) => ({ ...l, saleNum: idx + 1, label: `Sale #${idx + 1}` }));
  };

  const chartData = getChartData();

  const allSizesChartData = selectedSize === 'All' ? (() => {
    // Group and sort each size independently so every line starts at sale #1
    const bySize = {};
    allSales
      .filter(l => l.date_sold && (l.sold_price || l.price))
      .forEach(l => {
        const size = getSize(l);
        const price = parsePrice(l.sold_price || l.price);
        if (!size || isNaN(price)) return;
        if (!bySize[size]) bySize[size] = [];
        bySize[size].push({ date: l.date_sold, price });
      });
    // Sort each size chronologically
    Object.values(bySize).forEach(arr => arr.sort((a, b) => new Date(a.date) - new Date(b.date)));
    // Build chart: x-axis = sale # within each size, all starting at 1
    const maxLen = Math.max(...Object.values(bySize).map(arr => arr.length), 0);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      const point = { saleNum: i + 1, label: 'Sale #' + (i + 1) };
      Object.entries(bySize).forEach(([size, arr]) => {
        if (i < arr.length) {
          point['size_' + size] = arr[i].price;
          point['date_' + size] = arr[i].date;
        }
      });
      result.push(point);
    }
    return result;
  })() : null;

  const sizeBreakdown = allSales.filter(l => l.sale_size && (l.sold_price || l.price) && !isTc(l) && !isPlug(l)).reduce((acc, l) => {
    const price = parsePrice(l.sold_price || l.price);
    if (isNaN(price)) return acc;
    if (!acc[l.sale_size]) acc[l.sale_size] = [];
    acc[l.sale_size].push(price);
    return acc;
  }, {});

  const plugBreakdown = allSales.filter(l => (l.sold_price || l.price) && isPlug(l)).reduce((acc, l) => {
    const price = parsePrice(l.sold_price || l.price);
    if (isNaN(price)) return acc;
    if (!acc['Plug/Starter']) acc['Plug/Starter'] = [];
    acc['Plug/Starter'].push(price);
    return acc;
  }, {});

  const tcBreakdown = listings.filter(l => l.sold_price && isTc(l)).reduce((acc, l) => {
    const packSize = l.tissue_culture_sale_pack_size || 'Unknown';
    const price = parsePrice(l.sold_price);
    if (isNaN(price)) return acc;
    const key = `${packSize} pack`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(price);
    return acc;
  }, {});

  const fetchEtsyListings = async () => {
    setEtsyLoading(true); setEtsyError(null);
    try {
      const res = await fetch('/.netlify/functions/etsy-search?plant=' + encodeURIComponent(decoded));
      const data = await res.json();
      setEtsyListings(Array.isArray(data) ? data : []);
    } catch (err) { setEtsyError('Failed to fetch Etsy listings.'); }
    setEtsyLoading(false);
  };

  const fetchEbayListings = async () => {
    setEbayLoading(true); setEbayError(null);
    try {
      const res = await fetch('/.netlify/functions/ebay-sold?plant=' + encodeURIComponent(decoded));
      const data = await res.json();
      setEbayListings(Array.isArray(data) ? data : []);
    } catch (err) { setEbayError('Failed to fetch eBay listings.'); }
    setEbayLoading(false);
  };

  const fetchForSale = async () => {
    setForSaleLoading(true);
    const { data } = await supabase
      .from('inventory')
      .select('*, sellers(business_name, palmstreet_url)')
      .eq('is_public', true)
      .eq('status', 'available')
      .ilike('common_name', ilikePattern)
      .order('date_listed', { ascending: false });
    // Compute verified badge per listing
    if (data && data.length > 0) {
      const withBadge = await Promise.all(data.map(async (l) => {
        let q = supabase.from('plants').select('sold_price').eq('status','approved').ilike('common_name', l.common_name);
        if (l.sale_type) q = q.ilike('sale_type', l.sale_type);
        if (l.sale_size) q = q.eq('sale_size', String(l.sale_size));
        const { data: pd } = await q.limit(30);
        if (!pd || pd.length < 3) return { ...l, _verified: false };
        const nums = pd.map(p => parseFloat(String(p.sold_price).replace(/[^0-9.]/g,''))).filter(n => !isNaN(n) && n > 0);
        if (nums.length < 3) return { ...l, _verified: false };
        const avg = nums.reduce((a,b)=>a+b,0)/nums.length;
        const lp = parseFloat(String(l.price||'').replace(/[^0-9.]/g,''));
        return { ...l, _verified: !isNaN(lp) && lp > 0 && Math.abs(lp - avg)/avg <= 0.25 };
      }));
      setForSaleListings(withBadge);
    } else {
      setForSaleListings([]);
    }
    setForSaleLoading(false);
  };

  const fetchStoreSightings = async () => {
    setStoreLoading(true);
    try {
      const { data } = await supabase
        .from('store_sightings')
        .select('*')
        .ilike('common_name', ilikePattern)
        .eq('status', 'approved')
        .order('date_seen', { ascending: false });
      setStoreSightings(data || []);
    } catch (err) { console.error('Error fetching store sightings:', err); }
    setStoreLoading(false);
  };

  // Combined + filtered sale records (community + member sales)
  const combinedListings = [
    ...listings.map(l => ({ ...l, _isMember: false })),
    ...normalizedMemberSales,
  ]
    .filter(l => filterType === 'All' || l.sale_type === filterType)
    .filter(l => filterSize === 'All' || (filterSize === 'TC' ? isTc(l) : filterSize === 'Plug' ? isPlug(l) : l.sale_size === filterSize))
    .filter(l => filterPattern === 'All' || l.variegation_pattern === filterPattern)
    .sort((a, b) => {
      if (!a.date_sold) return 1;
      if (!b.date_sold) return -1;
      return sortOrder === 'newest'
        ? new Date(b.date_sold) - new Date(a.date_sold)
        : new Date(a.date_sold) - new Date(b.date_sold);
    });

  const tabStyle = (tab, color = '#4a7c59', activeGrad = 'linear-gradient(135deg, #2c4a2e, #4a7c59)') => ({
    padding: mobile ? '8px 12px' : '10px 20px',
    borderRadius: '20px', cursor: 'pointer',
    fontSize: mobile ? '10px' : '12px',
    fontWeight: 700, letterSpacing: mobile ? '0.5px' : '1px',
    textTransform: 'uppercase', fontFamily: 'Lato, sans-serif', transition: 'all 0.2s',
    background: activeTab === tab ? activeGrad : 'transparent',
    color: activeTab === tab ? '#f5f0e8' : color,
    border: activeTab === tab ? 'none' : `2px solid ${color}`,
    whiteSpace: 'nowrap', flexShrink: 0,
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const point = payload[0]?.payload;
      return (
        <div style={{ background: '#fff', border: '1px solid #4a7c59', borderRadius: '10px', padding: '10px 16px', fontFamily: 'Lato, sans-serif', fontSize: '13px', boxShadow: '0 4px 12px rgba(44,74,46,0.15)' }}>
          <p style={{ color: '#4a5e4b', marginBottom: '4px', fontWeight: 700 }}>{point?.date ? formatDate(point.date) : ''}</p>
          {point?.label && <p style={{ color: '#8b9e8c', fontSize: '11px', marginBottom: '6px' }}>{point.label}</p>}
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color, fontWeight: 700, margin: '2px 0' }}>{p.name}: {formatPrice(p.value)}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  const cardStyle = {
    background: '#ffffff', borderRadius: '20px', padding: mobile ? '16px' : '24px',
    boxShadow: '0 2px 16px rgba(44,74,46,0.1)', marginBottom: '24px',
    border: '1px solid rgba(143,185,154,0.3)',
  };

  const thStyle = {
    padding: mobile ? '6px 8px' : '10px 16px', fontSize: mobile ? '9px' : '11px',
    letterSpacing: '1px', textTransform: 'uppercase', color: '#4a5e4b',
    fontFamily: 'Lato, sans-serif', fontWeight: 700, textAlign: 'left',
    borderBottom: '2px solid rgba(143,185,154,0.3)',
  };

  const tdStyle = {
    padding: mobile ? '8px 8px' : '12px 16px',
    fontSize: mobile ? '12px' : '14px',
    borderBottom: '1px solid rgba(143,185,154,0.15)',
  };

  const totalSalesCount = listings.length + memberSales.length;

  // Build JSON-LD structured data for Google
  const prices = listings
    .map(l => parseFloat(String(l.sold_price || '').replace(/[^0-9.]/g, '')))
    .filter(p => !isNaN(p) && p > 0);
  const jsonLd = prices.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: decoded,
    description: `Real sold prices for ${decoded}. ${listings.length} sales recorded on True Plant Value.`,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: Math.min(...prices).toFixed(2),
      highPrice: Math.max(...prices).toFixed(2),
      offerCount: listings.length,
    },
  } : null;

  const ogImage = headerPhoto;
  const ogDesc = `${decoded} sold prices — ${listings.length} real sales recorded. See price history, charts, and market trends on True Plant Value.`;

  return (
    <>
    <div style={{ paddingBottom: '60px' }}>
      <SEOMeta
        title={decoded}
        description={ogDesc}
        image={ogImage}
        url={`/plant/${slugify(decoded || '')}`}
        type="product"
      />
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      )}
      {/* HEADER */}
      <div style={{
        padding: mobile ? '24px 16px' : '40px 40px 36px',
        marginBottom: '32px', borderRadius: '0 0 32px 32px',
        boxShadow: '0 8px 32px rgba(44,74,46,0.18)',
        background: 'linear-gradient(135deg, #2c4a2e 0%, #4a7c59 100%)',
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        alignItems: mobile ? 'stretch' : 'center', gap: mobile ? '20px' : '32px',
      }}>
        <div style={{ flex: 1, order: mobile ? 2 : 1 }}>
          <button onClick={() => navigate('/search')} style={{
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
            color: '#f5f0e8', padding: '8px 18px', borderRadius: '20px', cursor: 'pointer',
            fontSize: '13px', fontFamily: 'Lato, sans-serif', marginBottom: '16px',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>← Back to Search</button>
          <div style={{ fontSize: '13px', letterSpacing: '4px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>🌿 Sale History</div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '28px' : '38px', color: '#f5f0e8', fontWeight: 700, lineHeight: 1.1 }}>{decoded}</h1>
          {listings.length > 0 && listings[0].scientific_name && (
            <p style={{ color: 'rgba(245,240,232,0.55)', fontFamily: 'Lato, sans-serif', fontSize: mobile ? '13px' : '15px', fontStyle: 'italic', fontWeight: 300, marginTop: '6px' }}>
              {listings[0].scientific_name}
            </p>
          )}
          {!loading && (
            <p style={{ color: '#8fb99a', marginTop: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 300, fontSize: '14px' }}>
              {totalSalesCount} sale{totalSalesCount !== 1 ? 's' : ''} recorded
              {mostSoldSize && <span> · Most sold: <strong>{mostSoldSize === 'TC' ? 'Tissue Culture (TC)' : `${mostSoldSize}"`}</strong></span>}
              {memberSales.length > 0 && <span> · <strong>{memberSales.length}</strong> member sale{memberSales.length !== 1 ? 's' : ''}</span>}
            </p>
          )}
        </div>
        {headerPhoto && (
          <div style={{ flexShrink: 0, order: mobile ? 1 : 2, width: mobile ? '100%' : '220px' }}>
            <img src={headerPhoto} alt={decoded} onClick={() => setSelectedPhoto(headerPhoto)} style={{
              width: '100%', height: mobile ? '200px' : '220px', objectFit: 'cover',
              borderRadius: '20px', border: '3px solid rgba(245,240,232,0.4)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)', cursor: 'pointer', display: 'block',
            }} />
          </div>
        )}
      </div>

      <div style={{ padding: mobile ? '0 12px' : '0 16px' }}>

        {/* PRICE HISTORY CHART */}
        {!loading && chartData.length >= 1 && (
          <div style={cardStyle}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e2d1f', marginBottom: '4px' }}>Price History</h2>
            <p style={{ fontSize: '13px', color: '#4a5e4b', marginBottom: '16px', fontFamily: 'Lato, sans-serif' }}>Filter by size or type to see price trends</p>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {availableSizes.map(size => (
                <button key={size} onClick={() => setSelectedSize(size)} style={{
                  padding: mobile ? '4px 10px' : '6px 14px', borderRadius: '20px', cursor: 'pointer',
                  fontSize: mobile ? '11px' : '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif',
                  border: selectedSize === size ? 'none' : '2px solid #c4a882',
                  background: selectedSize === size ? (size === 'TC' ? 'linear-gradient(135deg, #7c4a7c, #a06ca0)' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)') : size === mostSoldSize ? '#f7f3ec' : 'transparent',
                  color: selectedSize === size ? '#f5f0e8' : size === 'TC' ? '#7c4a7c' : '#2c4a2e',
                  borderColor: size === 'TC' && selectedSize !== size ? '#c4a8c4' : '#c4a882',
                }}>
                  {size === 'All' ? 'All Sizes' : size === 'TC' ? 'Tissue Culture (TC)' : size === 'Plug' ? 'Plug/Starter' : `${size}" Pots`}
                  {size === mostSoldSize && size !== 'All' && <span style={{ marginLeft: '3px' }}>🔥</span>}
                </button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={mobile ? 200 : 250}>
              {selectedSize === 'All' ? (
                <LineChart data={allSizesChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(143,185,154,0.3)" />
                  <XAxis dataKey="saleNum" tick={{ fontSize: 9, fontFamily: 'Lato, sans-serif', fill: '#4a5e4b' }} tickLine={false} label={{ value: 'Sale # (chronological)', position: 'insideBottom', offset: -2, fontSize: 9, fill: '#8b9e8c', fontFamily: 'Lato, sans-serif' }} />
                  <YAxis tick={{ fontSize: 9, fontFamily: 'Lato, sans-serif', fill: '#4a5e4b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {uniqueSizes.map(size => (
                    <Line key={size} type="monotone" dataKey={`size_${size}`} name={size === 'TC' ? 'Tissue Culture (TC)' : size === 'Plug' ? 'Plug/Starter' : `${size}" Pots`} stroke={COLORS[size] || '#4a7c59'} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={true} />
                  ))}
                </LineChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(143,185,154,0.3)" />
                  <XAxis dataKey="saleNum" tick={{ fontSize: 9, fontFamily: 'Lato, sans-serif', fill: '#4a5e4b' }} tickLine={false} label={{ value: 'Sale # (chronological)', position: 'insideBottom', offset: -2, fontSize: 9, fill: '#8b9e8c', fontFamily: 'Lato, sans-serif' }} />
                  <YAxis tick={{ fontSize: 9, fontFamily: 'Lato, sans-serif', fill: '#4a5e4b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="price" stroke={COLORS[selectedSize] || '#2c4a2e'} strokeWidth={2.5} dot={{ fill: COLORS[selectedSize] || '#4a7c59', r: 4 }} activeDot={{ r: 6 }} name={selectedSize === 'TC' ? 'Tissue Culture (TC)' : selectedSize === 'Plug' ? 'Plug/Starter' : `${selectedSize}" Pots`} />
                </LineChart>
              )}
            </ResponsiveContainer>
            <p style={{ fontSize: '11px', color: '#8b9e8c', fontFamily: 'Lato, sans-serif', textAlign: 'center', marginTop: '4px' }}>
              Sales are plotted in chronological order. Hover any point to see the exact date.
            </p>
          </div>
        )}

        {/* SIZE BREAKDOWN */}
        {!loading && Object.keys(sizeBreakdown).length > 0 && (
          <div style={cardStyle}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e2d1f', marginBottom: '4px' }}>🌱 Price by Size</h2>
            <p style={{ fontSize: '12px', color: '#4a5e4b', marginBottom: '12px', fontFamily: 'Lato, sans-serif' }}>Average sold price per pot size</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Size', 'Avg', 'Low', 'High', '#'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(sizeBreakdown).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).map(([size, prices]) => (
                  <tr key={size} style={{ background: size === mostSoldSize ? '#f0f7f1' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{size}" {size === mostSoldSize && '🔥'}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{formatPrice(avg(prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.min(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.max(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{prices.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TC BREAKDOWN */}
        {!loading && Object.keys(tcBreakdown).length > 0 && (
          <div style={cardStyle}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e2d1f', marginBottom: '4px' }}>🧫 Tissue Culture (TC) Packs</h2>
            <p style={{ fontSize: '12px', color: '#4a5e4b', marginBottom: '12px', fontFamily: 'Lato, sans-serif' }}>Average sold price per TC pack size</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Pack', 'Avg', 'Low', 'High', '#'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(tcBreakdown).map(([pack, prices]) => (
                  <tr key={pack}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{pack}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{formatPrice(avg(prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.min(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.max(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{prices.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PLUG BREAKDOWN */}
        {!loading && Object.keys(plugBreakdown).length > 0 && (
          <div style={cardStyle}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e2d1f', marginBottom: '4px' }}>🌱 Plug/Starter Sales</h2>
            <p style={{ fontSize: '12px', color: '#4a5e4b', marginBottom: '12px', fontFamily: 'Lato, sans-serif' }}>Average sold price for plug/starter plants</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Type', 'Avg', 'Low', 'High', '#'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(plugBreakdown).map(([key, prices]) => (
                  <tr key={key}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{key}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#2c4a2e' }}>{formatPrice(avg(prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.min(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{formatPrice(Math.max(...prices))}</td>
                    <td style={{ ...tdStyle, color: '#4a5e4b' }}>{prices.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FILTERS */}
        {activeTab === 'mine' && !loading && combinedListings.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', background: '#fff', cursor: 'pointer' }}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
            <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setVisibleRecords(10); }} style={{ padding: '8px 12px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', background: '#fff', cursor: 'pointer' }}>
              <option value="All">All Types</option>
              <option value="Potted">Potted</option>
              <option value="Cutting">Cutting</option>
              <option value="Plug/Starter">Plug/Starter</option>
              <option value="Tissue Culture (TC)">Tissue Culture (TC)</option>
            </select>
            <select value={filterSize} onChange={(e) => { setFilterSize(e.target.value); setVisibleRecords(10); }} style={{ padding: '8px 12px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', background: '#fff', cursor: 'pointer' }}>
              <option value="All">All Sizes</option>
              <option value="2">2"</option>
              <option value="3">3"</option>
              <option value="4">4"</option>
              <option value="6">6"</option>
              <option value="8">8"</option>
              <option value="10">10"</option>
              <option value="12">12"</option>
              <option value="TC">Tissue Culture</option>
              <option value="Plug">Plug/Starter</option>
            </select>
            <select value={filterPattern} onChange={(e) => { setFilterPattern(e.target.value); setVisibleRecords(10); }} style={{ padding: '8px 12px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', background: '#fff', cursor: 'pointer' }}>
              <option value="All">All Patterns</option>
              <option value="Half Moon">Half Moon</option>
              <option value="Marble">Marble</option>
              <option value="Speckled/Splash">Speckled/Splash</option>
              <option value="Sectoral">Sectoral</option>
              <option value="Tricolor">Tricolor</option>
              <option value="Ghost">Ghost</option>
              <option value="Full">Full</option>
            </select>
            <span style={{ fontSize: '13px', color: '#4a5e4b', fontFamily: 'Lato, sans-serif' }}>
              Showing <strong>{combinedListings.length}</strong> of {totalSalesCount} records
            </span>
          </div>
        )}

        {/* TABS */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          <button style={tabStyle('mine')} onClick={() => setActiveTab('mine')}>Sale Records</button>
          <button style={tabStyle('forsale', '#8b6914', 'linear-gradient(135deg, #8b6914, #c4a882)')} onClick={() => { setActiveTab('forsale'); if (forSaleListings.length === 0) fetchForSale(); }}>For Sale 🌿</button>
          <button style={tabStyle('etsy')} onClick={() => { setActiveTab('etsy'); if (etsyListings.length === 0) fetchEtsyListings(); }}>Etsy</button>
          <button style={tabStyle('ebay')} onClick={() => { setActiveTab('ebay'); if (ebayListings.length === 0) fetchEbayListings(); }}>eBay</button>
          <button style={tabStyle('store', '#8b6914', 'linear-gradient(135deg, #8b6914, #c4a882)')} onClick={() => { setActiveTab('store'); if (storeSightings.length === 0) fetchStoreSightings(); }}>Spotted in Wild 🔍</button>
        </div>

        {/* SALE RECORDS TABLE */}
        {activeTab === 'mine' && (
          loading ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>Loading sale records...</p>
          : combinedListings.length === 0 ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>No sale records found.</p>
          : mobile ? (
            <div>
              {combinedListings.slice(0, visibleRecords).map((listing, index) => (
                <div key={index} style={{ background: listing._isMember ? '#f0f7ff' : '#ffffff', borderRadius: '16px', padding: '16px', marginBottom: '12px', boxShadow: '0 2px 12px rgba(44,74,46,0.08)', border: listing._isMember ? '1px solid rgba(74,124,200,0.3)' : '1px solid rgba(143,185,154,0.3)' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {listing.image_url ? (
                      <img src={listing.image_url} alt="plant" onClick={() => setSelectedPhoto(listing.image_url)}
                        style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '10px', cursor: 'pointer', flexShrink: 0, border: '2px solid rgba(143,185,154,0.4)' }} />
                    ) : (
                      <div style={{ width: '64px', height: '64px', borderRadius: '10px', background: '#f7f3ec', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>🌿</div>
                    )}
                    <div style={{ flex: 1 }}>
                      {listing._isMember && (
                        <span style={{ display: 'inline-block', background: '#4a7cc8', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', borderRadius: '10px', padding: '2px 8px', marginBottom: '6px', fontFamily: 'Lato, sans-serif' }}>
                          Member Sale
                        </span>
                      )}
                      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1e2d1f', fontWeight: 700, marginBottom: '4px' }}>{listing.common_name || '—'}</div>
                      <div style={{ fontSize: '20px', color: '#2c4a2e', fontWeight: 700, marginBottom: '8px' }}>{formatPrice(listing.sold_price || listing.price)}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        {[
                          ['Date', formatDate(listing.date_sold)],
                          ['Type', listing.sale_type],
                          ['Size', listing.sale_size ? `${listing.sale_size}"` : null],
                          ['TC Pack', listing.tissue_culture_sale_pack_size],
                          ['Variegation', listing.variegation_level],
                          ['Source', listing._isMember ? 'Member Sale' : listing.source],
                          ['Notes', listing.notes],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label}>
                            <span style={{ fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>{label}: </span>
                            <span style={{ fontSize: '13px', color: '#1e2d1f', fontFamily: 'Lato, sans-serif' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '20px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)' }}>
                    {['Photo', 'Date Sold', 'Common Name', 'Variegation', 'Pattern', 'Type', 'Size', 'TC Pack', 'Sale Price', 'Source'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', color: '#f5f0e8', fontFamily: 'Lato, sans-serif', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {combinedListings.slice(0, visibleRecords).map((listing, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(143,185,154,0.2)', background: listing._isMember ? '#f0f7ff' : index % 2 === 0 ? '#ffffff' : '#f9fcf9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        {listing.image_url ? (
                          <img src={listing.image_url} alt="plant" onClick={() => setSelectedPhoto(listing.image_url)}
                            style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: '2px solid rgba(143,185,154,0.4)', transition: 'transform 0.2s' }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'} />
                        ) : <span style={{ color: '#c4a882' }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#2c4a2e', fontWeight: 600 }}>{formatDate(listing.date_sold)}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#1e2d1f', fontFamily: 'Playfair Display, serif' }}>
                        {listing.common_name || '—'}
                        {listing._isMember && (
                          <span style={{ display: 'inline-block', background: '#4a7cc8', color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', borderRadius: '8px', padding: '2px 6px', marginLeft: '8px', fontFamily: 'Lato, sans-serif', verticalAlign: 'middle' }}>
                            Member
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                        {listing.variegation_level ? (
                          <span style={{ color: listing.variegation_level === 'Low' ? '#c0392b' : listing.variegation_level === 'Mid' ? '#8b6914' : '#4a7c59', fontWeight: 700 }}>
                            {listing.variegation_level === 'Low' ? '🔴' : listing.variegation_level === 'Mid' ? '🟡' : '🟢'} {listing.variegation_level}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{listing.variegation_pattern || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{listing.sale_type || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{listing.sale_size ? `${listing.sale_size}"` : '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{listing.tissue_culture_sale_pack_size || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#2c4a2e', fontWeight: 700 }}>{formatPrice(listing.sold_price || listing.price)}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{listing._isMember ? 'Member Sale' : (listing.source || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* SHOW MORE / LESS */}
        {activeTab === 'mine' && !loading && combinedListings.length > visibleRecords && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={() => setVisibleRecords(v => v + 10)} style={{ background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '24px', padding: '10px 28px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Show More ({combinedListings.length - visibleRecords} remaining) ↓
            </button>
          </div>
        )}
        {activeTab === 'mine' && !loading && visibleRecords > 10 && combinedListings.length <= visibleRecords && combinedListings.length > 10 && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={() => setVisibleRecords(10)} style={{ background: 'transparent', border: '2px solid #c4a882', color: '#4a5e4b', borderRadius: '24px', padding: '10px 28px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Show Less ↑
            </button>
          </div>
        )}

        {/* ETSY */}
        {activeTab === 'etsy' && (
          etsyLoading ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>Fetching Etsy listings...</p>
          : etsyError ? <p style={{ color: '#c0392b', fontStyle: 'italic' }}>{etsyError}</p>
          : etsyListings.length === 0 ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>No Etsy listings found.</p>
          : (
            <div style={{ overflowX: 'auto', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '20px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #f56400, #ff8c00)' }}>
                    {['Title', 'Price', 'Link'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', color: '#ffffff', fontFamily: 'Lato, sans-serif', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {etsyListings.map((listing, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(245,100,0,0.1)', background: index % 2 === 0 ? '#ffffff' : '#fff8f5' }}>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1e2d1f', maxWidth: '400px' }}>{listing.title}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#f56400', fontWeight: 700 }}>${listing.price}</td>
                      <td style={{ padding: '14px 16px' }}><a href={listing.url} target="_blank" rel="noreferrer" style={{ color: '#f56400', fontWeight: 700, fontSize: '13px' }}>View on Etsy →</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* EBAY */}
        {activeTab === 'ebay' && (
          ebayLoading ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>Fetching eBay listings...</p>
          : ebayError ? <p style={{ color: '#c0392b', fontStyle: 'italic' }}>{ebayError}</p>
          : ebayListings.length === 0 ? <p style={{ color: '#4a7c59', fontStyle: 'italic' }}>No eBay listings found.</p>
          : (
            <div style={{ overflowX: 'auto', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '20px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #e53238, #f5af02)' }}>
                    {['Title', 'Price', 'Link'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', color: '#ffffff', fontFamily: 'Lato, sans-serif', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ebayListings.map((listing, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(229,50,56,0.1)', background: index % 2 === 0 ? '#ffffff' : '#fff5f5' }}>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1e2d1f', maxWidth: '400px' }}>{listing.title}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#e53238', fontWeight: 700 }}>${listing.price}</td>
                      <td style={{ padding: '14px 16px' }}><a href={listing.url} target="_blank" rel="noreferrer" style={{ color: '#e53238', fontWeight: 700, fontSize: '13px' }}>View on eBay →</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* STORE SIGHTINGS */}
        {activeTab === 'forsale' && (
          <div>
            {forSaleLoading ? (
              <p style={{ color: '#8b6914', fontStyle: 'italic', fontFamily: 'Lato, sans-serif' }}>Loading listings...</p>
            ) : forSaleListings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '20px', border: '1px solid rgba(143,185,154,0.3)' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌿</div>
                <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '15px', marginBottom: '8px' }}>No members are currently selling this plant.</p>
                <p style={{ color: '#8b9e8c', fontFamily: 'Lato, sans-serif', fontSize: '13px' }}>Check back soon or list your own!</p>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  {forSaleListings.slice(0, 3).map((listing) => {
                    const formatP = (v) => { if (!v) return '—'; const n = parseFloat(String(v).replace(/[^0-9.]/g,'')); return isNaN(n) ? v : '$'+n.toFixed(2); };
                    return (
                      <div key={listing.id} onClick={() => navigate(`/listing/${listing.id}/${listing.sale_type ? listing.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${listing.sale_size ? '-'+listing.sale_size+'in' : ''}-${(listing.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`)}
                        style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(139,105,20,0.1)', border: '1px solid rgba(139,105,20,0.2)', cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div style={{ position: 'relative' }}>
                          {listing.image_url ? (
                            <img src={listing.image_url} alt={listing.common_name} style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ height: '160px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>🌿</div>
                          )}
                          {listing._verified && (
                            <img src="/trueplantvaluebadge.png" alt="Verified True Plant Value" style={{ position: 'absolute', top: '8px', right: '8px', width: '52px', height: '52px', pointerEvents: 'none' }} />
                          )}
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#8b6914', fontWeight: 700, marginBottom: '4px' }}>{formatP(listing.price)}</div>
                          <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#4a5e4b', marginBottom: '4px' }}>
                            {[listing.sale_type, listing.sale_size ? `${listing.sale_size}"` : null].filter(Boolean).join(' · ')}
                          </div>
                          {listing.sellers?.business_name && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#8b9e8c' }}>By {listing.sellers.business_name}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {forSaleListings.length > 3 && (
                  <div style={{ textAlign: 'center' }}>
                    <button onClick={() => navigate(`/for-sale?q=${encodeURIComponent(decoded)}`)} style={{ background: 'transparent', border: '2px solid #8b6914', color: '#8b6914', borderRadius: '24px', padding: '12px 28px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                      View all {forSaleListings.length} listings →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'store' && (
          storeLoading ? <p style={{ color: '#8b6914', fontStyle: 'italic' }}>Loading store sightings...</p>
          : storeSightings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '20px', border: '1px solid rgba(143,185,154,0.3)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <p style={{ color: '#8b6914', fontFamily: 'Lato, sans-serif', fontSize: '16px', marginBottom: '16px' }}>No store sightings yet for this plant!</p>
              <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '13px' }}>Spotted this plant at Home Depot, Lowes, or another store? Share it with the community!</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '20px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #8b6914, #c4a882)' }}>
                    {['Photo', 'Store', 'Price', 'Date Seen', 'Location', 'Notes'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', color: '#ffffff', fontFamily: 'Lato, sans-serif', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {storeSightings.map((s, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(139,105,20,0.1)', background: index % 2 === 0 ? '#ffffff' : '#fdfaf5' }}>
                      <td style={{ padding: '14px 16px' }}>
                        {s.image_url ? (
                          <img src={s.image_url} alt="store sighting" onClick={() => setSelectedPhoto(s.image_url)}
                            style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: '2px solid rgba(139,105,20,0.3)' }} />
                        ) : <span style={{ color: '#c4a882' }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#8b6914', fontWeight: 700 }}>{s.store || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#2c4a2e', fontWeight: 700 }}>{s.price ? `$${parseFloat(s.price.replace(/[^0-9.]/g,'')).toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{s.date_seen ? new Date(s.date_seen).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{[s.city, s.state].filter(Boolean).join(', ') || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#4a5e4b' }}>{s.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* YOU MIGHT ALSO LIKE */}
        {relatedPlants.length > 0 && (
          <div style={{ marginTop: '40px' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', marginBottom: '6px' }}>You Might Also Like</h2>
            <p style={{ fontSize: '13px', color: '#4a5e4b', fontFamily: 'Lato, sans-serif', marginBottom: '16px' }}>Similar {relatedPlants[0]?.genus || ''} varieties tracked on the site</p>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '14px' }}>
              {relatedPlants.map((p) => (
                <div key={p.common_name} onClick={() => navigate(`/plant/${slugify(p.common_name)}`)}
                  style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(44,74,46,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(44,74,46,0.08)'; }}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.common_name} loading="lazy" style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', height: '120px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>🌿</div>
                  )}
                  <div style={{ padding: '12px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '13px', color: '#1e3d20', marginBottom: '6px', lineHeight: 1.3 }}>{p.common_name}</h3>
                    <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a7c59', fontWeight: 700 }}>{formatPrice(p.sold_price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PHOTO LIGHTBOX */}
        {selectedPhoto && (
          <div onClick={() => setSelectedPhoto(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <div>
              <img src={selectedPhoto} alt="plant enlarged" loading="lazy" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px', objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} />
              <div style={{ textAlign: 'center', color: '#f5f0e8', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginTop: '12px', opacity: 0.7 }}>Click anywhere to close</div>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Message Modal */}
      {pdMessageModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setPdMessageModal(null); }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', marginBottom: '6px' }}>Contact Seller</h3>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '20px' }}>Re: <strong>{pdMessageModal.common_name}</strong> — {(() => { const n = parseFloat(String(pdMessageModal.price).replace(/[^0-9.]/g,'')); return isNaN(n) ? pdMessageModal.price : '$'+n.toFixed(2); })()}</p>
            {pdMsgSent ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '8px' }}>Message Sent!</p>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '20px' }}>The seller will reply to your email address.</p>
                <button onClick={() => setPdMessageModal(null)} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', padding: '10px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Close</button>
              </div>
            ) : (
              <>
                {[{ label: 'Your Name', key: 'buyer_name', placeholder: 'e.g. Jane Smith' }, { label: 'Your Email', key: 'buyer_email', placeholder: 'your@email.com' }].map(({ label, key, placeholder }) => (
                  <div key={key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '5px', fontFamily: 'Lato, sans-serif' }}>{label}</label>
                    <input value={pdMsgForm[key]} onChange={(e) => setPdMsgForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                      style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #c4a882', borderRadius: '10px', fontSize: '14px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                ))}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '5px', fontFamily: 'Lato, sans-serif' }}>Message</label>
                  <textarea value={pdMsgForm.message} onChange={(e) => setPdMsgForm(f => ({ ...f, message: e.target.value }))} placeholder="Hi, I'm interested in this plant. Is it still available?" rows={4}
                    style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #c4a882', borderRadius: '10px', fontSize: '14px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }} />
                </div>
                {pdMsgError && <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginBottom: '12px' }}>{pdMsgError}</p>}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button disabled={pdMsgSending} onClick={async () => {
                    if (!pdMsgForm.buyer_name || !pdMsgForm.buyer_email || !pdMsgForm.message) { setPdMsgError('Please fill in all fields.'); return; }
                    setPdMsgSending(true); setPdMsgError('');
                    const { error } = await supabase.from('messages').insert({ listing_id: pdMessageModal.id, seller_id: pdMessageModal.seller_id, buyer_name: pdMsgForm.buyer_name, buyer_email: pdMsgForm.buyer_email, message: pdMsgForm.message });
                    if (error) { setPdMsgError('Failed to send. Please try again.'); } else { setPdMsgSent(true); }
                    setPdMsgSending(false);
                  }} style={{ flex: 1, padding: '12px', background: pdMsgSending ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700, cursor: pdMsgSending ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>
                    {pdMsgSending ? 'Sending...' : '💬 Send Message'}
                  </button>
                  <button onClick={() => setPdMessageModal(null)} style={{ padding: '12px 20px', background: 'transparent', border: '1.5px solid #c4a882', color: '#4a5e4b', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PlantDetails;